import { captionCueSchema, limits, type CaptionCue, type TranscriptSegment } from "@veyso/core";

/**
 * Captions are rendered by Remotion, never burned in by ffmpeg, so they stay
 * editable right up to the final render. Cues are clamped to the section and any
 * segment that falls entirely outside it is dropped.
 */
export function buildCaptions(segments: TranscriptSegment[], duration: number, offset = 0): CaptionCue[] {
  const cues: CaptionCue[] = [];

  for (const [index, segment] of segments.entries()) {
    if (cues.length >= limits.maxCaptions) break;

    const start = Math.max(0, segment.start - offset);
    const end = Math.min(duration, segment.end - offset);
    if (end <= start) continue;

    const parsed = captionCueSchema.safeParse({
      id: `cue-${index + 1}`,
      start: Number(start.toFixed(3)),
      end: Number(end.toFixed(3)),
      text: segment.text.trim(),
      words: segment.words
        ?.map((word) => ({
          start: Number(Math.max(0, word.start - offset).toFixed(3)),
          end: Number(Math.min(duration, word.end - offset).toFixed(3)),
          text: word.text,
        }))
        .filter((word) => word.end > word.start),
    });

    if (parsed.success) cues.push(parsed.data);
  }

  return cues;
}

/** A line of narration and the slot it occupies on the finished video's clock. */
export interface NarrationLine {
  text: string;
  startAt: number;
  duration: number;
}

/** Roughly two lines of readable caption. Longer and it covers the video. */
const MAX_CUE_CHARACTERS = 90;

/**
 * Splits a line at the last sensible break before the limit.
 *
 * Prefers the end of a clause, because a caption that breaks mid-phrase reads
 * worse than one that is a little short.
 */
function chunk(text: string, limit = MAX_CUE_CHARACTERS): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const chunks: string[] = [];
  let current = "";

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > limit && current) {
      chunks.push(current);
      current = word;
    } else {
      current = candidate;
    }
    // A clause ending comfortably into the line is a better break than the limit.
    if (/[.!?]$/.test(current) && current.length > limit * 0.45) {
      chunks.push(current);
      current = "";
    }
  }
  if (current) chunks.push(current);
  return chunks.length > 0 ? chunks : [text];
}

/**
 * Captions for the narration, rather than for the recording's own audio.
 *
 * The two are different texts: the transcript is what the presenter ad-libbed
 * while recording, and the script is the rewrite the voice actually speaks.
 * Captioning the transcript means the viewer reads one sentence while hearing
 * another — the words disagree, and so does the timing, because the rewrite is
 * a different length.
 *
 * Timing is distributed across each clip by character count. That assumes an
 * even speaking rate within a line, which is close enough over a sentence or
 * two; the clip's own start and duration are exact, so drift cannot accumulate
 * across the video.
 */
export function captionsFromNarration(lines: readonly NarrationLine[], offset = 0): CaptionCue[] {
  const cues: CaptionCue[] = [];

  for (const line of lines) {
    const text = line.text.trim();
    if (!text || line.duration <= 0) continue;

    const pieces = chunk(text);
    const characters = pieces.reduce((total, piece) => total + piece.length, 0) || 1;
    let cursor = line.startAt - offset;

    for (const piece of pieces) {
      if (cues.length >= limits.maxCaptions) return cues;

      const span = (piece.length / characters) * line.duration;
      const start = cursor;
      const end = cursor + span;
      cursor = end;

      // Word timings are spread the same way, so highlight-as-spoken still has
      // something to follow.
      const words = piece.split(/\s+/).filter(Boolean);
      const wordCharacters = words.reduce((total, word) => total + word.length, 0) || 1;
      let wordCursor = start;

      const parsed = captionCueSchema.safeParse({
        id: `cue-${cues.length + 1}`,
        start: Number(start.toFixed(3)),
        end: Number(end.toFixed(3)),
        text: piece,
        words: words.map((word) => {
          const wordSpan = (word.length / wordCharacters) * span;
          const wordStart = wordCursor;
          wordCursor += wordSpan;
          return {
            start: Number(wordStart.toFixed(3)),
            end: Number(wordCursor.toFixed(3)),
            text: word,
          };
        }),
      });

      if (parsed.success) cues.push(parsed.data);
    }
  }

  return cues;
}
